import { addDays, getDay, setHours, setMinutes, startOfDay } from "date-fns";

// ---------------------------------------------------------------------------
// New slot geometry types (engine-facing)
// ---------------------------------------------------------------------------

export type TimeSlotDef = {
  startHour: number;
  endHour: number;
  preferredHour: number | null;
  disabled: boolean;
};

export const TIME_SLOTS: Record<string, TimeSlotDef> = {
  EARLY_MORNING: { startHour: 0,  endHour: 5,  preferredHour: null, disabled: true },
  MORNING:       { startHour: 6,  endHour: 9,  preferredHour: 9,    disabled: false },
  MIDDAY:        { startHour: 10, endHour: 13, preferredHour: 12,   disabled: false },
  AFTERNOON:     { startHour: 14, endHour: 17, preferredHour: 16,   disabled: false },
  EVENING:       { startHour: 18, endHour: 21, preferredHour: 19,   disabled: false },
  LATE_NIGHT:    { startHour: 22, endHour: 23, preferredHour: 22,   disabled: false },
};

// ---------------------------------------------------------------------------
// Shared slot priority (engine-facing, priority-ordered)
// ---------------------------------------------------------------------------

export type SlotEntry = {
  startHour: number;
  endHour: number;
  preferredHour: number;
};

// Single schedule shared by all platforms: days fill chronologically, hours
// follow this priority order.
export const SLOT_PRIORITY: SlotEntry[] = [
  { startHour: 18, endHour: 21, preferredHour: 19 },
  { startHour: 14, endHour: 17, preferredHour: 16 },
  { startHour: 10, endHour: 13, preferredHour: 12 },
  { startHour: 6,  endHour: 9,  preferredHour: 9 },
  { startHour: 22, endHour: 23, preferredHour: 22 },
];

// ---------------------------------------------------------------------------
// Display slots (UI label derivation)
// ---------------------------------------------------------------------------

export const DISPLAY_SLOTS = [
  { label: "Early Morning", startHour: 0,  endHour: 5 },
  { label: "Morning",       startHour: 6,  endHour: 9 },
  { label: "Midday",        startHour: 10, endHour: 13 },
  { label: "Afternoon",     startHour: 14, endHour: 17 },
  { label: "Evening",       startHour: 18, endHour: 21 },
  { label: "Late Night",    startHour: 22, endHour: 23 },
] as const;

export function slotLabelForHour(hour: number): string {
  return DISPLAY_SLOTS.find(s => hour >= s.startHour && hour <= s.endHour)?.label ?? "Other";
}

// ---------------------------------------------------------------------------
// Fan-out utility
// ---------------------------------------------------------------------------

export function fanOutHours(preferred: number, start: number, end: number): number[] {
  const hours: number[] = [preferred];
  for (let offset = 1; offset <= end - start; offset++) {
    if (preferred + offset <= end) hours.push(preferred + offset);
    if (preferred - offset >= start) hours.push(preferred - offset);
  }
  return hours;
}

// ---------------------------------------------------------------------------
// Calendar backward-compat layer
// ---------------------------------------------------------------------------

export interface TimeSlotDefinition {
  id: string;
  label: string;
  startHour: number;
  endHour: number;
  /** Engine-suggested posting hour within the band (see TIME_SLOTS). */
  preferredHour: number | null;
  color: string;
  isRecommended: (dayOfWeek: number) => boolean;
  description: string;
  isDisabled?: boolean;
}

// Recommendation is day-independent: the top-priority band (Evening) is
// recommended every day.
function isRecommendedForRange(startHour: number, endHour: number): boolean {
  const top = SLOT_PRIORITY[0];
  return top.startHour === startHour && top.endHour === endHour;
}

export const CALENDAR_SLOTS: TimeSlotDefinition[] = [
  {
    id: "early-morning",
    label: "Early Morning",
    startHour: 0,
    endHour: 5,
    preferredHour: TIME_SLOTS.EARLY_MORNING.preferredHour,
    color: "border-slate-200",
    isRecommended: () => false,
    description: "Early morning (12-5 AM)",
    isDisabled: true,
  },
  {
    id: "morning",
    label: "Morning",
    startHour: 6,
    endHour: 9,
    preferredHour: TIME_SLOTS.MORNING.preferredHour,
    color: "border-slate-200",
    isRecommended: () => isRecommendedForRange(6, 9),
    description: "Morning slot (6-9 AM)",
  },
  {
    id: "midday",
    label: "Midday",
    startHour: 10,
    endHour: 13,
    preferredHour: TIME_SLOTS.MIDDAY.preferredHour,
    color: "border-slate-200",
    isRecommended: () => isRecommendedForRange(10, 13),
    description: "Midday slot (10 AM-1 PM)",
  },
  {
    id: "afternoon",
    label: "Afternoon",
    startHour: 14,
    endHour: 17,
    preferredHour: TIME_SLOTS.AFTERNOON.preferredHour,
    color: "border-slate-200",
    isRecommended: () => isRecommendedForRange(14, 17),
    description: "Afternoon slot (2-5 PM)",
  },
  {
    id: "evening",
    label: "Evening",
    startHour: 18,
    endHour: 21,
    preferredHour: TIME_SLOTS.EVENING.preferredHour,
    color: "border-slate-200",
    isRecommended: () => isRecommendedForRange(18, 21),
    description: "Evening slot (6-9 PM)",
  },
  {
    id: "late-night",
    label: "Late Night",
    startHour: 22,
    endHour: 23,
    preferredHour: TIME_SLOTS.LATE_NIGHT.preferredHour,
    color: "border-slate-200",
    isRecommended: () => isRecommendedForRange(22, 23),
    description: "Late night (10 PM-12 AM)",
  },
];

// ---------------------------------------------------------------------------
// Core utilities
// ---------------------------------------------------------------------------

export function getISODay(date: Date): number {
  const day = getDay(date);
  return day === 0 ? 7 : day;
}

export function findSlotForDate(date: Date): TimeSlotDefinition | undefined {
  const hour = date.getHours();
  return CALENDAR_SLOTS.find(
    (slot) => hour >= slot.startHour && hour <= slot.endHour,
  );
}

export function normalizeToSlotStart(date: Date): Date {
  const normalized = new Date(date);
  normalized.setMinutes(0, 0, 0);
  return normalized;
}

export function createOccupiedSlotSet(occupiedDates: Date[]): Set<number> {
  return new Set(
    occupiedDates
      .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
      .map((date) => normalizeToSlotStart(date).getTime()),
  );
}

const ALL_ISO_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;
const CUSTOM_CADENCE_MAX_VIDEOS_PER_DAY = 3;

export function getRecommendedSlotCapacityForISODay(dayOfWeek: number): number {
  if (!Number.isInteger(dayOfWeek) || dayOfWeek < 1 || dayOfWeek > 7) {
    return 0;
  }

  return CALENDAR_SLOTS.filter(
    (slot) => !slot.isDisabled && slot.isRecommended(dayOfWeek),
  ).length;
}

function getAllowedISODaySet(allowedISODays?: number[]): Set<number> {
  const sanitizedDays = (allowedISODays ?? ALL_ISO_DAYS).filter(
    (day) => Number.isInteger(day) && day >= 1 && day <= 7,
  );

  return new Set(sanitizedDays.length > 0 ? sanitizedDays : ALL_ISO_DAYS);
}

function getVideosPerISODayMap(
  videosPerISODay?: Partial<Record<number, number>>,
): Map<number, number> {
  if (!videosPerISODay) return new Map();

  const entries = Object.entries(videosPerISODay)
    .map(([isoDay, videosCount]) => [Number(isoDay), videosCount] as const)
    .filter((entry): entry is readonly [number, number] => {
      const [isoDay, videosCount] = entry;
      return (
        Number.isInteger(isoDay) &&
        isoDay >= 1 &&
        isoDay <= 7 &&
        typeof videosCount === "number" &&
        Number.isFinite(videosCount)
      );
    })
    .map(([isoDay, videosCount]) => {
      const clampedCount = Math.min(
        CUSTOM_CADENCE_MAX_VIDEOS_PER_DAY,
        Math.max(0, Math.round(videosCount)),
      );
      return [isoDay, clampedCount] as const;
    });

  return new Map(entries);
}

function hasOccupiedNormalizedSlot(
  occupiedSlots: Set<number>,
  date: Date,
): boolean {
  return occupiedSlots.has(normalizeToSlotStart(date).getTime());
}

// ---------------------------------------------------------------------------
// Legacy scheduling functions (Calendar.tsx, BulkCreatePublicationForm.tsx)
// ---------------------------------------------------------------------------

// CALENDAR_SLOTS reordered to match the shared SLOT_PRIORITY (disabled slots
// excluded).
const PRIORITY_ORDERED_CALENDAR_SLOTS: TimeSlotDefinition[] = SLOT_PRIORITY.map(
  (entry) =>
    CALENDAR_SLOTS.find(
      (s) => s.startHour === entry.startHour && s.endHour === entry.endHour,
    ),
).filter((s): s is TimeSlotDefinition => s != null && !s.isDisabled);

export function findNextRecommendedTimeSlot(
  fromDate: Date,
  occupiedSlots: Set<number> = new Set(),
  allowedISODays?: number[],
): Date {
  const now = new Date();
  const requestedDate = new Date(fromDate);
  const effectiveFromDate = requestedDate < now ? new Date(now) : requestedDate;
  const allowedISODaySet = getAllowedISODaySet(allowedISODays);

  // Try each slot band (in shared priority order) across days: the best band
  // is exhausted across the window before falling back to the next band.
  for (const slot of PRIORITY_ORDERED_CALENDAR_SLOTS) {
    let currentDate = new Date(effectiveFromDate);
    for (let i = 0; i < 14; i++) {
      const dayOfWeek = getISODay(currentDate);
      if (!allowedISODaySet.has(dayOfWeek)) {
        currentDate = addDays(startOfDay(currentDate), 1);
        continue;
      }

      const slotTime = setMinutes(
        setHours(new Date(currentDate), slot.startHour),
        0,
      );
      slotTime.setSeconds(0, 0);

      if (
        slotTime >= effectiveFromDate &&
        slotTime > now &&
        !hasOccupiedNormalizedSlot(occupiedSlots, slotTime)
      ) {
        return slotTime;
      }

      currentDate = addDays(startOfDay(currentDate), 1);
    }
  }

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);

  while (true) {
    if (!allowedISODaySet.has(getISODay(tomorrow))) {
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      continue;
    }

    if (!hasOccupiedNormalizedSlot(occupiedSlots, tomorrow)) {
      break;
    }

    const nextHour = tomorrow.getHours() + 1;
    if (nextHour > 23) {
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
    } else {
      tomorrow.setHours(nextHour, 0, 0, 0);
    }
  }

  return tomorrow;
}

export function distributeBulkPublicationSlots(params: {
  videosCount: number;
  videosPerDay: number;
  fromDate?: Date;
  occupiedDates?: Date[];
  allowedISODays?: number[];
  videosPerISODay?: Partial<Record<number, number>>;
}): Date[] {
  const {
    videosCount,
    videosPerDay,
    fromDate,
    occupiedDates = [],
    allowedISODays,
    videosPerISODay,
  } = params;

  if (videosCount <= 0) return [];

  const now = new Date();
  const effectiveFromDate = fromDate ? new Date(fromDate) : now;
  const occupiedSlots = createOccupiedSlotSet(occupiedDates);
  const videosPerISODayMap = getVideosPerISODayMap(videosPerISODay);
  const allowedISODaySet = getAllowedISODaySet(allowedISODays);
  const effectiveAllowedISODaySet =
    videosPerISODayMap.size > 0
      ? new Set(
          Array.from(videosPerISODayMap.entries())
            .filter(([_, videosCount]) => videosCount > 0)
            .map(([isoDay]) => isoDay),
        )
      : allowedISODaySet;
  if (videosPerISODayMap.size > 0 && effectiveAllowedISODaySet.size === 0) {
    return [];
  }

  const safeVideosPerDay = Math.max(1, videosPerDay);
  const results: Date[] = [];
  let dayCursor = startOfDay(effectiveFromDate > now ? effectiveFromDate : now);
  let safetyCounter = 0;

  while (results.length < videosCount && safetyCounter < 365) {
    const dayOfWeek = getISODay(dayCursor);
    if (!effectiveAllowedISODaySet.has(dayOfWeek)) {
      dayCursor = addDays(dayCursor, 1);
      safetyCounter += 1;
      continue;
    }
    const videosTargetForDay =
      videosPerISODayMap.size > 0
        ? (videosPerISODayMap.get(dayOfWeek) ?? 0)
        : safeVideosPerDay;
    if (videosTargetForDay <= 0) {
      dayCursor = addDays(dayCursor, 1);
      safetyCounter += 1;
      continue;
    }

    const allDaySlots = CALENDAR_SLOTS.filter((slot) => !slot.isDisabled).map(
      (slot) => {
        const slotTime = setMinutes(
          setHours(new Date(dayCursor), slot.startHour),
          0,
        );
        slotTime.setSeconds(0, 0);
        return { slot, slotTime };
      },
    );
    const occupiedCountForDay = allDaySlots.filter(({ slotTime }) =>
      hasOccupiedNormalizedSlot(occupiedSlots, slotTime),
    ).length;
    const remainingCapacity = Math.max(
      0,
      videosTargetForDay - occupiedCountForDay,
    );
    if (remainingCapacity <= 0) {
      dayCursor = addDays(dayCursor, 1);
      safetyCounter += 1;
      continue;
    }

    const availableSlots = allDaySlots.filter(({ slotTime }) => {
      if (results.length === 0 && slotTime < effectiveFromDate) return false;
      return (
        slotTime > now && !hasOccupiedNormalizedSlot(occupiedSlots, slotTime)
      );
    });

    const priorityIndex = new Map(
      PRIORITY_ORDERED_CALENDAR_SLOTS.map((s, i) => [s.id, i]),
    );
    const daySlotsToUse = [...availableSlots].sort(
      (a, b) =>
        (priorityIndex.get(a.slot.id) ?? Number.MAX_SAFE_INTEGER) -
        (priorityIndex.get(b.slot.id) ?? Number.MAX_SAFE_INTEGER),
    );

    daySlotsToUse.slice(0, remainingCapacity).forEach(({ slotTime }) => {
      if (results.length >= videosCount) return;
      results.push(slotTime);
      occupiedSlots.add(slotTime.getTime());
    });

    dayCursor = addDays(dayCursor, 1);
    safetyCounter += 1;
  }

  while (results.length < videosCount) {
    const next = findNextRecommendedTimeSlot(
      results.length > 0 ? results[results.length - 1] : effectiveFromDate,
      occupiedSlots,
      Array.from(effectiveAllowedISODaySet),
    );
    results.push(next);
    occupiedSlots.add(next.getTime());
  }

  return results;
}
